<template>
	<AppLayout :title="trans('settings.majors.title')">
		<Modal id="create-major-modal">
			<template #title>{{ trans('settings.majors.new.title') }}</template>
			<LabeledObject required>
				<template #label>{{ trans('settings.majors.name') }}</template>
				<input v-model="newMajor.name" type="text">
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-major-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Pagination :resource="majors"/>
		<Empty v-if="majors.data.length === 0"/>
		<CardGrid v-else>
			<Card v-for="major in majors.data" :key="`majors-${major.id}`">
				<LabeledObject required>
					<template #label>{{ trans('settings.majors.name') }}</template>
					<input v-model="major.name" type="text">
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('settings.majors.students') }}</template>
					<p>{{ major.college_programs_count }}</p>
				</LabeledObject>
				<template #footer>
					<SecondaryButton :color="Color.Green" @click.stop="save(major)">
						<i class="fa-solid fa-save mr-2"/>
						{{ trans('general.save') }}
					</SecondaryButton>
					<SoftButton v-if="major.college_programs_count === 0" :color="Color.Red" @click.stop="destroy(major)">
						<i class="fa-solid fa-trash mr-2"/>
						{{ trans('general.delete') }}
					</SoftButton>
				</template>
			</Card>
		</CardGrid>
		<Pagination :resource="majors"/>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import {type Major} from '@/Types/Settings/Major'
import Pagination from '@/Components/UI/Pagination.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type PaginatedModel} from '@/Types/Pagination/PaginatedModel'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	majors:PaginatedModel<Major>,
}>()

const working:Ref<boolean> = ref(false)
const majors:Ref<PaginatedModel<Major>> = ref(props.majors)
const newMajor:Ref<Major> = ref({
	name: '',
} as Major)

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('settings.majors.store'), newMajor.value).then(({data}:{data:Major}) => {
			majors.value.data.push(data)
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function save(major:Major):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('settings.majors.update', major.id), major).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function destroy(major:Major):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('settings.majors.delete', major.id)).then(() => {
			const index = majors.value.data.findIndex((m:Major) => m.id === major.id)
			if(index > -1){
				majors.value.data.splice(index, 1)
			}
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>